import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const validatePassword = (password: string) => {
  if (password.length < 8) return 'La nueva clave debe tener al menos 8 caracteres.';
  if (!/[A-Z]/.test(password)) return 'La nueva clave debe incluir al menos una mayuscula.';
  if (!/[a-z]/.test(password)) return 'La nueva clave debe incluir al menos una minuscula.';
  if (!/[0-9]/.test(password)) return 'La nueva clave debe incluir al menos un numero.';
  return null;
};

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data, error: sessionError }) => {
      if (!mounted) return;
      if (sessionError || !data.session) {
        setError('El link ya no es valido o expiro. Solicita uno nuevo.');
      }
      setCheckingSession(false);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const validation = validatePassword(password);
    if (validation) {
      setError(validation);
      return;
    }

    if (password !== confirmPassword) {
      setError('Las claves no coinciden.');
      return;
    }

    setIsLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        throw updateError;
      }

      await supabase.auth.signOut();
      setSuccess('Tu contraseña fue actualizada correctamente. Ya puedes cerrar esta pestaña y volver a la aplicación.');
    } catch (err: any) {
      setError(err?.message || 'No se pudo actualizar la clave.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-lock-password-line text-3xl text-teal-600"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {success ? 'Contraseña actualizada' : 'Actualiza tu clave'}
          </h1>
          <p className="text-gray-600">
            {success ? 'El cambio se realizó correctamente.' : 'Define una nueva contraseña para tu cuenta.'}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
            {success}
          </div>
        )}

        {!success && <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nueva clave</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="********"
                disabled={checkingSession || isLoading}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                <i className={`ri-eye-${showPassword ? 'off' : ''}-line text-xl`}></i>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar clave</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="********"
              disabled={checkingSession || isLoading}
              required
            />
          </div>

          <button
            type="submit"
            disabled={checkingSession || isLoading || !!success}
            className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {checkingSession ? 'Validando link...' : isLoading ? 'Actualizando...' : 'Guardar nueva clave'}
          </button>
        </form>}

        {!success && <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-sm text-teal-600 hover:text-teal-700 font-medium"
          >
            Volver al login
          </button>
        </div>}
      </div>
    </div>
  );
}


